import { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, DollarSign, Loader2, Pencil, Trash2 } from "lucide-react";
import { RentPayment } from "@/hooks/use-property-management";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { PaymentDetailDrawer } from "./PaymentDetailDrawer";
import { PropertyMeta } from "@/lib/property-meta";
import { PAYMENT_METHODS, PAYMENT_STATUSES } from "./payment-options";
import {
  getApprovedTenantNameForSavedProperty,
  rentedListingPropertyOptions,
} from "@/hooks/use-properties-with-rented-listing";
import { useRentals } from "@/hooks/use-rentals";
import { format, parseISO } from "date-fns";

interface Props {
  payments: RentPayment[];
  isLoading: boolean;
  portfolioProperties: { id: string; label: string }[];
  propertyMeta: Record<string, PropertyMeta>;
  onCreate: (p: Partial<RentPayment>) => void;
  onUpdate: (p: Partial<RentPayment> & { id: string }) => void;
  /**
   * Permanently removes the payment. `onSuccess` fires only when the delete
   * lands, so a failed one leaves the confirmation open (the toast explains why).
   */
  onDelete: (id: string, opts: { onSuccess: () => void }) => void;
  isSaving: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const STATUS_MAP: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  paid: "secondary",
  pending: "outline",
  late: "destructive",
  partial: "default",
};

export function PaymentsTab({
  payments,
  isLoading,
  portfolioProperties,
  propertyMeta,
  onCreate,
  onUpdate,
  onDelete,
  isSaving,
  isUpdating,
  isDeleting,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<RentPayment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RentPayment | null>(null);
  const [form, setForm] = useState<Partial<RentPayment>>({
    tenant_name: "",
    amount: 0,
    status: "pending",
  });

  const { listings, leads } = useRentals();
  const paymentPropertyOptions = useMemo(
    () => rentedListingPropertyOptions(portfolioProperties, listings),
    [portfolioProperties, listings],
  );

  // `propertyMeta` knows every pipeline property, so it goes first; the other
  // two only cover properties still on /properties or still listed for rent.
  const propertyLabelById = useMemo(() => {
    const labels = new Map(portfolioProperties.map((p) => [p.id, p.label]));
    for (const [id, meta] of Object.entries(propertyMeta)) {
      if (!labels.has(id)) labels.set(id, meta.address);
    }
    for (const l of listings) {
      if (!labels.has(l.pipeline_property_id))
        labels.set(l.pipeline_property_id, l.title);
    }
    return labels;
  }, [portfolioProperties, propertyMeta, listings]);

  // Only rented listings can take a *new* payment, but an existing one may point
  // at a property that has since been unrented — keep it selectable so editing
  // anything else doesn't force the payment onto a different property.
  const editPropertyOptions = useMemo(() => {
    const options = [...paymentPropertyOptions];
    const known = new Set(options.map((o) => o.id));
    for (const p of payments) {
      if (known.has(p.pipeline_property_id)) continue;
      known.add(p.pipeline_property_id);
      options.push({
        id: p.pipeline_property_id,
        label:
          propertyLabelById.get(p.pipeline_property_id) ??
          `Property ${p.pipeline_property_id.slice(0, 6)}`,
      });
    }
    return options;
  }, [paymentPropertyOptions, payments, propertyLabelById]);

  const totalCollected = payments
    .filter((p) => p.status === "paid")
    .reduce((s, p) => s + p.amount, 0);
  const totalPending = payments
    .filter((p) => p.status === "pending" || p.status === "late")
    .reduce((s, p) => s + p.amount, 0);

  // Keep the open drawer in sync with list refetches.
  useEffect(() => {
    if (!selected) return;
    const updated = payments.find((p) => p.id === selected.id);
    if (updated && updated !== selected) setSelected(updated);
  }, [payments, selected]);

  const handleSubmit = () => {
    if (
      !form.tenant_name ||
      !form.pipeline_property_id ||
      !form.amount ||
      !form.due_date
    )
      return;
    const paid_date =
      form.status === "paid" ? new Date().toISOString().split("T")[0] : null;
    onCreate({ ...form, paid_date });
    setDialogOpen(false);
    setForm({ tenant_name: "", amount: 0, status: "pending" });
  };

  const openDrawer = (p: RentPayment) => {
    setSelected(p);
    setDrawerOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    onDelete(deleteTarget.id, {
      onSuccess: () => {
        // Drop the drawer too if it was showing the row we just removed.
        if (selected?.id === deleteTarget.id) {
          setDrawerOpen(false);
          setSelected(null);
        }
        setDeleteTarget(null);
      },
    });
  };

  if (isLoading)
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">Collected</p>
            <p className="text-lg font-bold text-success">
              ${totalCollected.toLocaleString()}
            </p>
          </Card>
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">Outstanding</p>
            <p className="text-lg font-bold text-destructive">
              ${totalPending.toLocaleString()}
            </p>
          </Card>
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">Total Payments</p>
            <p className="text-lg font-bold text-foreground">
              {payments.length}
            </p>
          </Card>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          disabled={paymentPropertyOptions.length === 0}
          title={
            paymentPropertyOptions.length === 0
              ? "Record payments for properties with a rented listing."
              : undefined
          }
        >
          <Plus className="h-4 w-4 mr-2" /> Record Payment
        </Button>
      </div>

      {payments.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No payments recorded</p>
            <p className="text-sm text-muted-foreground/60">
              Record your first payment to start tracking rent
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-soft">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Paid Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Late Fee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => openDrawer(p)}
                >
                  <TableCell>
                    <div className="font-medium text-foreground">
                      {p.tenant_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {propertyLabelById.get(p.pipeline_property_id) ?? "—"}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    ${p.amount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(p.due_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {p.paid_date
                      ? format(parseISO(p.paid_date), "MMM d, yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell className="capitalize">
                    {p.payment_method?.replace("_", " ") || "—"}
                  </TableCell>
                  <TableCell
                    className={
                      p.late_fee ? "tabular-nums text-destructive" : ""
                    }
                  >
                    {p.late_fee ? `$${p.late_fee.toLocaleString()}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={STATUS_MAP[p.status] || "outline"}
                      className="capitalize"
                    >
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {/* The row itself opens the drawer — don't let these clicks bubble into it. */}
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.status !== "paid" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onUpdate({
                              id: p.id,
                              status: "paid",
                              paid_date: new Date().toISOString().split("T")[0],
                            })
                          }
                        >
                          Mark Paid
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label={`Edit payment for ${p.tenant_name}`}
                        onClick={() => openDrawer(p)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        aria-label={`Delete payment for ${p.tenant_name}`}
                        onClick={() => setDeleteTarget(p)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={form.pipeline_property_id || ""}
                onValueChange={(v) => {
                  const tenantName =
                    getApprovedTenantNameForSavedProperty(v, listings, leads) ??
                    "";
                  setForm({
                    ...form,
                    pipeline_property_id: v,
                    tenant_name: tenantName,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {paymentPropertyOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tenant Name</Label>
              <Input
                value={form.tenant_name || ""}
                onChange={(e) =>
                  setForm({ ...form, tenant_name: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  value={form.amount || ""}
                  onChange={(e) =>
                    setForm({ ...form, amount: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <DateInput
                  value={form.due_date || ""}
                  onChange={(e) =>
                    setForm({ ...form, due_date: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select
                  value={form.payment_method || ""}
                  onValueChange={(v) => setForm({ ...form, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !form.tenant_name ||
                !form.pipeline_property_id ||
                !form.amount ||
                !form.due_date ||
                isSaving
              }
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{" "}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentDetailDrawer
        payment={selected}
        open={drawerOpen}
        property={
          selected ? propertyMeta[selected.pipeline_property_id] : undefined
        }
        propertyOptions={editPropertyOptions}
        onOpenChange={setDrawerOpen}
        onUpdate={onUpdate}
        isSaving={isUpdating}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Delete this payment?"
        description={
          deleteTarget && (
            <>
              This permanently deletes the{" "}
              <span className="font-medium text-foreground">
                ${deleteTarget.amount.toLocaleString()}
              </span>{" "}
              payment from{" "}
              <span className="font-medium text-foreground">
                {deleteTarget.tenant_name}
              </span>{" "}
              due {format(parseISO(deleteTarget.due_date), "MMM d, yyyy")}. This
              can't be undone
              {deleteTarget.status === "paid"
                ? ", and it will lower the rental income reported on your Properties and Accounting pages."
                : "."}
            </>
          )
        }
        confirmLabel="Delete payment"
        isPending={isDeleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
